import {Flex, Center, Box, Text, useMediaQuery} from '@chakra-ui/react';
import {ResponsiveImageType} from 'react-datocms';
import {
  ProductModelHeroContentField,
  ProductModelHeroTitleField,
} from '../../graphql/datocms-generated';
import {BackButton} from '../global/back-button';
import {DatoCmsImage} from '../datocms/image';
import {PiecePanel} from './piece-panel';
import {HeroBodyCopy, HeroTitleCopy} from './story-structured-content';

interface ProductHeroProps {
  bgColor: string;
  accentColor: string;
  title?: ProductModelHeroTitleField;
  image?: ResponsiveImageType;
  price?: string;
  pieces?: string;
  body: ProductModelHeroContentField;
  setBackgroundImage?: boolean;
  desktopBackgroundImage?: ResponsiveImageType;
  mobileBackgroundImage?: ResponsiveImageType;
  partnershipLogo?: string;
  status: string;
}

const backGroundImageStyles = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: '100%',
  width: '100%',
  '&:before': {
    content: '""',
    backgroundImage:
      'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 62.5%)',
    zIndex: 1,
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
};

export const ProductHero: React.FC<ProductHeroProps> = ({
  bgColor,
  accentColor,
  title,
  image,
  price,
  pieces,
  body,
  setBackgroundImage,
  desktopBackgroundImage,
  mobileBackgroundImage,
  partnershipLogo,
  status,
}) => {
  const visibleOnMobile = {base: 'block', lg: 'none'};
  const visibleOnDesktop = {base: 'none', lg: 'block'};
  const [isLargerThan500] = useMediaQuery('(min-width: 500px)');

  return (
    <Flex
      id="top"
      background={bgColor}
      color="white"
      px={{base: 5, md: 5, lg: '60px'}}
      pb={{base: 50, lg: '80px'}}
      pt={{base: '80px', lg: '30px'}}
      flexDir="column"
      backgroundPosition="top"
      backgroundSize="cover"
      position="relative"
    >
      <Flex
        display={{base: 'none', lg: 'flex'}}
        alignSelf="flex-start"
        pb={{base: 10, lg: 18}}
        pl={{lg: 5}}
        zIndex={2}
      >
        <BackButton />
      </Flex>

      <Center
        flexDir={{base: 'column', lg: 'row'}}
        height={setBackgroundImage ? 'calc(100vh - 116px)' : ''}
      >
        {title && (
          <Box
            display={visibleOnMobile}
            mt="60px"
            maxW="400px"
            textAlign="center"
            mb={6}
            zIndex={2}
          >
            {status === 'COMING_SOON' && (
              <Text textStyle="h4_1c" pt={{base: 0, xl: '50px'}}>
                Coming Soon
              </Text>
            )}
            <HeroTitleCopy text={title} />
          </Box>
        )}

        {setBackgroundImage && (
          <Center mb={6}>
            <Box width="350px" maxW="100%" pt={{base: '0%', md: '100%'}} />
          </Center>
        )}

        {image && !setBackgroundImage && (
          <Center mb={6}>
            <Box maxW="322px" w="100%">
              <DatoCmsImage responsiveImage={image} />
            </Box>
          </Center>
        )}

        <Flex
          display={visibleOnDesktop}
          flexDir="column"
          alignItems={{base: 'center', lg: 'flex-start'}}
          justifyContent={{base: 'center', lg: 'flex-start'}}
          pl={{base: 0, lg: '72px'}}
          zIndex={2}
        >
          <Box maxW={{base: '450px', lg: '500px'}}>
            {status === 'COMING_SOON' && (
              <Text textStyle="h4_1c" pt={{base: 0, xl: '50px'}}>
                Coming Soon
              </Text>
            )}
            {title && <HeroTitleCopy text={title} />}
            {body && <HeroBodyCopy text={body} />}
          </Box>
          {price && pieces && (
            <Box>
              <PiecePanel
                status={status}
                color={accentColor}
                price={price}
                pieces={pieces}
                image={partnershipLogo || undefined}
              />
            </Box>
          )}
        </Flex>

        {price && pieces && (
          <Box
            maxW="515px"
            w="100%"
            display={visibleOnMobile}
            zIndex={2}
            mt={setBackgroundImage ? {base: 'auto', md: '40px'} : 0}
          >
            <PiecePanel
              status={status}
              color={accentColor}
              price={price}
              pieces={pieces}
              image={partnershipLogo || undefined}
            />
          </Box>
        )}

        {setBackgroundImage && mobileBackgroundImage && !isLargerThan500 && (
          <Box sx={backGroundImageStyles}>
            <DatoCmsImage
              responsiveImage={mobileBackgroundImage}
              style={{position: 'static'}}
              layout="fill"
              objectFit="cover"
              objectPosition="50% 0%"
            />
          </Box>
        )}

        {setBackgroundImage && desktopBackgroundImage && isLargerThan500 && (
          <Box sx={backGroundImageStyles}>
            <DatoCmsImage
              responsiveImage={desktopBackgroundImage}
              style={{position: 'static'}}
              layout="fill"
              objectFit="cover"
              objectPosition="50% 0%"
            />
          </Box>
        )}

        {body && (
          <Box maxW="450px" display={visibleOnMobile} zIndex={2}>
            <HeroBodyCopy text={body} />
          </Box>
        )}
      </Center>
    </Flex>
  );
};
